// src/pages/settings/Profile.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Grid,
  Box,
  Button,
  TextField,
  LinearProgress,
  Avatar,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { userService } from "../../services/userService";
import apiService from "../../services/api";

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [user, setUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [formValues, setFormValues] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    address: "",
    company_name: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const userData = await userService.getCurrentUser();
      setUser(userData);
      setFormValues({
        full_name: userData.full_name || "",
        email: userData.email || "",
        phone_number: userData.phone_number || "",
        address: userData.address || "",
        company_name: userData.company_name || "",
      });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to load profile", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiService.put("/users/me", formValues);
      setUser(response.data);
      setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to update profile";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setSnackbar({ open: true, message: "New passwords do not match", severity: "error" });
      return;
    }
    setChangingPassword(true);
    try {
      await apiService.post("/users/me/change-password", passwordForm);
      setSnackbar({ open: true, message: "Password changed successfully!", severity: "success" });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to change password";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setChangingPassword(false);
    }
  };

  const formatRole = (role) => {
    if (!role) return "";
    return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: 48,
                  bgcolor: "primary.main",
                  margin: "0 auto 16px",
                }}
              >
                {user?.full_name?.charAt(0)?.toUpperCase() || "?"}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {user?.full_name}
              </Typography>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {formatRole(user?.role)}
              </Typography>
              {user?.company_name && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {user.company_name}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                @{user?.username}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Member since{" "}
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          {/* Edit Profile */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Edit Profile
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="full_name"
                    label="Full Name"
                    value={formValues.full_name}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="email"
                    label="Email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="phone_number"
                    label="Phone Number"
                    value={formValues.phone_number}
                    onChange={handleChange}
                    fullWidth
                    autoComplete="off"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="company_name"
                    label="Company Name"
                    value={formValues.company_name}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    name="address"
                    label="Address"
                    value={formValues.address}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    autoComplete="off"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Change Password */}
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handlePasswordSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    name="current_password"
                    label="Current Password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    autoComplete="current-password"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="new_password"
                    label="New Password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    autoComplete="new-password"
                    helperText="Min 12 chars, uppercase, lowercase, number, special char"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="confirm_password"
                    label="Confirm New Password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    autoComplete="new-password"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={changingPassword ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                    disabled={changingPassword}
                  >
                    {changingPassword ? "Changing..." : "Change Password"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
